<script setup lang="ts">
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import type { PdpEcommerceBlockProductInfoRedesignProps } from './PdpEcommerceBlockProductInfoRedesign.props'

const props = withDefaults(
  defineProps<PdpEcommerceBlockProductInfoRedesignProps>(),
  {
    fitting: null,
    isDenim: false,
  }
)
const emit = defineEmits<{
  'open-virtual-mirror': [eventDetail: string]
}>()

const { currentWebsite } = useWebsite()
const { ts } = useI18n()
const { openDialog } = useDialog()

const { closeDialog: closeSidebar } = useDialog(
  SIDEBAR_IDS.pdpEcommerceblockProductInfoAndDetailsSideSlide
)
/** V001 `eventElement01`. The legacy PDP collapsed both into `product_info_entrypoints`. */
const VM_ENTRYPOINT = {
  /** Inline link in the info & details copy, inside the fit and sizing accordion. */
  detailsAccordion: 'details_accordion',
  /** "Prova virtuale" progressive label at the bottom of the product info block. */
  virtualAccordion: 'virtual_accordion',
} as const

const openVirtualMirror = (entrypoint: string) => {
  closeSidebar()
  emit('open-virtual-mirror', entrypoint)
}

const VM_CLASS = 'open-virtual-mirror'

const virtualMirrorHtml = computed(() =>
  ts('pdp.productDetails.informationSection.virtualMirrorDescription', {
    className: VM_CLASS,
  })
)

function onVirtualMirrorDescClick(e: MouseEvent) {
  if ((e.target as HTMLElement).closest(`.${VM_CLASS}`))
    openVirtualMirror(VM_ENTRYPOINT.detailsAccordion)
}

const formatComposition = (composition: string) => {
  if (!composition) return ''

  return composition
    .split(';')
    .map(part => part.trim())
    .filter(part => part.length > 0)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('<br>')
}

const hasCompositionOrCare = computed(
  () =>
    !!props.productDetails?.composition ||
    (!!props.productCare?.description && !!props.productCare?.items?.length)
)

// Used by the "discover more" CTA, commented out in the template until the
// material origin sidesheet is implemented (see the TODO next to it).
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const openMaterialOrigin = () => {
  openDialog(SIDEBAR_IDS.pdpEcommerceBlockMaterialOriginSideSlide)
}

const hasTechnicalDetails = computed(() =>
  props.productDetails.technicalDetails?.some(detail => !!detail?.trim())
)

/* Only one info section can be open at a time */
type InfoSection = 'details' | 'composition' | 'fit' | 'faq'
const openSection = ref<InfoSection | null>(null)
const toggleSection = (section: InfoSection, open: boolean) => {
  openSection.value = open ? section : null
}
</script>

<template>
  <div class="flex flex-col">
    <div class="flex flex-col lg:gap-6 xl:gap-16">
      <div class="lg:gap-sm gap-lg xl:gap-lg flex flex-col">
        <div class="flex flex-col gap-10">
          <div class="flex flex-col gap-2">
            <!-- Product details (read more) -->
            <div
              v-if="productDetails?.editoralDescription || hasTechnicalDetails"
              class="gap-xs flex flex-col"
            >
              <h2 class="text-medium-6">
                {{
                  $ts(
                    'pdp.productDetails.informationSection.productDetailsTitle'
                  )
                }}
              </h2>
              <UtilsInfo
                is-pdp-redesign
                :expanded="openSection === 'details'"
                :max-character="300"
                :content="productDetails.editoralDescription"
                :expanded-label="
                  ts('pdp.productDetails.informationSection.readMore')
                "
                :reduced-label="
                  ts('pdp.productDetails.informationSection.readLess')
                "
                cta-class="!text-link-8"
                :has-gradient="true"
                @update:expanded="toggleSection('details', $event)"
              >
                <p v-if="productDetails.editoralDescription">
                  {{ productDetails.editoralDescription }}
                </p>

                <div
                  v-if="hasTechnicalDetails"
                  class="gap-xxs mt-4 flex flex-col"
                >
                  <ul
                    v-for="techInfo in productDetails.technicalDetails"
                    :key="techInfo"
                  >
                    <li class="text-light-7">
                      <UtilsMarkdown :content="techInfo" />
                    </li>
                  </ul>
                </div>

                <!-- PRODUCT CODE -->
                <div class="gap-xxs mt-4 flex flex-col">
                  <h3 class="text-book-7">
                    {{
                      $ts('pdp.productDetails.informationSection.productCode')
                    }}
                  </h3>
                  <div class="text-light-7">
                    {{ productDetails.SkuCode }}
                  </div>
                </div>
              </UtilsInfo>
            </div>

            <!-- Accordion section -->
            <!-- Composition and care -->
            <PdpEcommerceBlockAccordion
              v-if="hasCompositionOrCare"
              :open="openSection === 'composition'"
              :label="
                $ts('pdp.productDetails.informationSection.compositionAndCare')
              "
              @update:open="toggleSection('composition', $event)"
            >
              <div class="gap-lg flex flex-col">
                <div
                  v-if="productDetails?.composition"
                  class="gap-xxs flex flex-col"
                >
                  <h3 class="text-book-7">
                    {{ $ts('pdp.productDetails.informationSection.materials') }}
                  </h3>
                  <div
                    class="text-light-7"
                    v-html="formatComposition(productDetails.composition)"
                  />
                  <!-- TODO: Uncomment this when the material origin sidebar is implemented -->
                  <!-- <AtomsCta
                      anatomy="link"
                      class="mt-1 self-start"
                      @click="openMaterialOrigin"
                    >
                      <template #label>
                        {{
                          $ts('pdp.productDetails.informationSection.discoverMore')
                        }}
                      </template>
                    </AtomsCta> -->
                </div>

                <div
                  v-if="productCare?.description || productCare?.items?.length"
                  class="gap-sm flex flex-col"
                >
                  <h3 class="text-book-7">
                    {{ $ts('pdp.productCare') }}
                  </h3>
                  <p v-if="productCare.description" class="text-light-7">
                    {{ productCare.description }}
                  </p>
                  <div v-if="productCare.items?.length" class="space-y-4">
                    <div
                      v-for="(item, idx) in productCare.items"
                      :key="item.id || idx"
                    >
                      <div class="flex items-center gap-4">
                        <div v-if="item.image" class="h-6 w-6 shrink-0">
                          <NuxtPicture
                            :src="item.image"
                            class="object-contain"
                            provider="cloudinary"
                            sizes="sm:100vw md:50vw lg:33vw"
                            alt=""
                          />
                        </div>
                        <p v-if="item.shortDescription" class="text-medium-7">
                          {{ item.shortDescription }}
                        </p>
                      </div>
                      <p v-if="item.longDescription" class="text-light-7 mt-1">
                        {{ item.longDescription }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </PdpEcommerceBlockAccordion>

            <!-- Fit and sizing -->
            <PdpEcommerceBlockAccordion
              v-if="fitting"
              :open="openSection === 'fit'"
              :label="$ts('pdp.productDetails.informationSection.fitAndSizing')"
              @update:open="toggleSection('fit', $event)"
            >
              <PdpEcommerceBlockFitScale
                :fitting="fitting"
                :is-denim="isDenim"
              />

              <!-- VIRTUAL MIRROR -->
              <div v-if="hasVirtualMirror" class="gap-xxs mt-4 flex flex-col">
                <h3 class="text-book-7">
                  {{
                    $ts('pdp.productDetails.informationSection.virtualMirror')
                  }}
                </h3>
                <div
                  class="text-light-7 cursor-default"
                  @click="onVirtualMirrorDescClick"
                  v-html="virtualMirrorHtml"
                />
              </div>
            </PdpEcommerceBlockAccordion>

            <!-- FAQ -->
            <PdpEcommerceBlockAccordion
              :open="openSection === 'faq'"
              :label="$ts('pdp.productDetails.informationSection.faq')"
              @update:open="toggleSection('faq', $event)"
            />

            <!-- Product Focus / Virtual Mirror (unchanged ProgressiveLabels) -->
            <PdpEcommerceBlockProgressiveLabel
              v-if="!!productFocus?.items?.length"
              class="text-medium-6 !py-2"
              :label="$ts('pdp.productFocus')"
              @click="
                () =>
                  openDialog(SIDEBAR_IDS.pdpEcommerceBlockProductFocusSideSlide)
              "
            />
            <PdpEcommerceBlockProgressiveLabel
              v-if="hasVirtualMirror"
              class="text-medium-6 !py-2"
              :label="$ts('pdp.virtualMirror.label')"
              @click="openVirtualMirror(VM_ENTRYPOINT.virtualAccordion)"
            />

            <!-- Empty div for Alhena Widget -->
            <div id="alhena-widget-pdp"></div>
          </div>

          <div
            class="pdp-product-focus flex flex-col gap-2"
            v-if="!!editorialProductFocus?.length"
          >
            <PdpProductFocusTrackingProvider
              v-for="(deliveryKey, index) of editorialProductFocus"
              :key="deliveryKey"
              :position="index + 1"
            >
              <EditorialModulesWrapper :delivery-key="deliveryKey" />
            </PdpProductFocusTrackingProvider>
          </div>
          <EditorialModulesWrapper
            :delivery-key="`${currentWebsite}-service-strip-pdp`"
          />
        </div>
      </div>
    </div>

    <!-- <PdpEcommerceBlockMaterialOriginSideSlide /> -->
    <PdpEcommerceBlockProductFocusSideSlide
      v-show="!!productFocus?.items?.length"
      :items="productFocus?.items ?? []"
    />
  </div>
</template>

<style scoped lang="scss">
:deep(.strip-anchors-carousel) {
  padding-left: 0;
  padding-right: 0;
}

@media screen and (min-width: 1024px) {
  :deep(.strip-anchors-carousel) {
    padding: 0;
  }
}

:deep(.open-virtual-mirror) {
  @apply text-link-7 cursor-pointer font-medium underline;
}

.pdp-product-focus :deep(.border-b.gap-px) {
  border-bottom: 0;
  gap: 0;
}
</style>
